import { useState } from 'react'
import { StoreVariant, StoreModifier } from '../types'
import { AvailabilityAction, AvailabilityGroupScope, ItemType } from './types'

interface PendingStockAction {
  id: string
  name: string
  action: AvailabilityAction
  type: ItemType
  groupName: string
}

interface UseAvailabilityGroupModalParams {
  storeVariants: StoreVariant[]
  storeModifiers: StoreModifier[]
  executeBulkAction: (
    itemType: ItemType,
    ids: string[],
    name: string,
    action: AvailabilityAction,
    updateEntireAvailabilityGroup?: boolean
  ) => void
}

export const useAvailabilityGroupModal = ({
  storeVariants,
  storeModifiers,
  executeBulkAction
}: UseAvailabilityGroupModalParams) => {
  const [visible, setVisible] = useState(false)
  const [pendingAction, setPendingAction] = useState<PendingStockAction | null>(
    null
  )

  const handleDismiss = () => {
    setVisible(false)
    setPendingAction(null)
  }

  const applyToGroup = (pending: PendingStockAction) => {
    const { id, type, name, action } = pending

    executeBulkAction(type, [id], name, action, true)
  }

  const applyToSingle = (pending: PendingStockAction) => {
    executeBulkAction(pending.type, [pending.id], pending.name, pending.action)
  }

  const handleConfirm = (scope: AvailabilityGroupScope) => {
    if (!pendingAction) return
    setVisible(false)

    const apply =
      scope === AvailabilityGroupScope.GROUP ? applyToGroup : applyToSingle
    apply(pendingAction)

    setPendingAction(null)
  }

  const toggleState =
    (type: ItemType) =>
    (action: AvailabilityAction, itemId: string, name: string) => {
      const id =
        type === ItemType.PRODUCT
          ? storeVariants.find((sv) => sv.id === itemId)?.variant_id
          : storeModifiers.find((sm) => sm.id === itemId)?.modifier_id

      if (!id) {
        return
      }

      const groupName =
        type === ItemType.PRODUCT
          ? storeVariants.find((sv) => sv.id === itemId)?.product_variant
              ?.product?.availability_group
          : storeModifiers.find((sm) => sm.id === itemId)?.modifier
              ?.availability_group

      if (groupName) {
        setPendingAction({ id, name, action, type, groupName })
        setVisible(true)
        return
      }

      executeBulkAction(type, [id], name, action)
    }

  return {
    visible,
    pendingAction,
    handleDismiss,
    handleConfirm,
    toggleState
  }
}
